import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableLoading
} from "@/components/ui/table";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { Route as ReportAvailableRoute } from "@/routes/(inventory)/branch/_layout/reports/customer-wise-revenue/index";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { MoveRightIcon } from "lucide-react";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { useCustomerRevenueData } from "../-hooks/useCustomerRevenueData";
import { useState } from "react";

interface Column {
  key: string;
  label: string;
}

const Component = () => {
  const searchParams = ReportAvailableRoute.useSearch();
  const { data, isLoading, error, isFetching } = useCustomerRevenueData();
  const navigate = useNavigate();

  // TableCell and TableHead wrappers for consistent styling
  const StyledTableCell = ({ className = "", ...props }) => (
    <TableCell className={cn(tableCellClass, "py-0 font-medium", className)} {...props} />
  );
  const StyledTableHead = ({ className = "", ...props }) => (
    <TableHead className={cn(tableHeadClass, className)} {...props} />
  );

  const columns: Column[] = [
    { key: "customer_name", label: "Customer Name" },
    { key: "phone_number", label: "Phone Number" },
    { key: "status", label: "Status" },
    { key: "number_of_visits", label: "Number of Visits" },
    { key: "last_visit_date", label: "Last Visit Date" },
    { key: "lifetime_revenue", label: "Lifetime Revenue" },
    { key: "total_refunds", label: "Total Refunds" },
    { key: "net_revenue", label: "Net Revenue" },
    { key: "total_cogs", label: "Total COGS" },
    { key: "gross_profit", label: "Gross Profit" },
    { key: "gross_profit_percentage", label: "Gross Profit Percentage" },
    { key: "average_basket_value", label: "Average Basket Value" },
  ];

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>)
  );


  return (
    <>
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => {
                    navigate({
                      to: "/branch/reports",
                    });
                  }}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  Reports Overview
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Customer Wise Revenue Report</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <hr className="my-4" />

        <div className="mt-4 border rounded-lg bg-white overflow-hidden">
        {isLoading ? (
          <TableLoading columns={columns} visibleColumns={visibleColumns} />
        ) : !data?.items || data.items.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-gray-500">No entries found</div>
          </div>
        ) : (
          <>  
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <StyledTableHead>Customer Name</StyledTableHead>
                <StyledTableHead>Phone Number</StyledTableHead>
                <StyledTableHead>Status</StyledTableHead>
                <StyledTableHead>Number of Visits</StyledTableHead>
                <StyledTableHead>Last Visit Date</StyledTableHead>
                <StyledTableHead>Lifetime Revenue</StyledTableHead>
                <StyledTableHead>Total Refunds</StyledTableHead>
                <StyledTableHead>Net Revenue</StyledTableHead>
                <StyledTableHead>Total COGS</StyledTableHead>
                <StyledTableHead>Gross Profit</StyledTableHead>
                <StyledTableHead>Gross Profit Percentage</StyledTableHead>
                <StyledTableHead>Average Basket Value</StyledTableHead>
                <StyledTableHead></StyledTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item, index) => {
                const isLastRow = index === data.totalCount - 1;
                return (
                  <TableRow key={index} className="font-medium">
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.customerName}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.customerPhone}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.customerStatus}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.numberOfVists}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{dayjs(item.lastVisitDate?.toDate()).format("DD/MM/YYYY")}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.lifetimeRevenue)}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.totalRefunds)}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.netRevenue)}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.totalCogs)}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.grossProfit)}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.grossMarginPercentage.toFixed(2)}%</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.averageBasketValue)}</StyledTableCell>
                    <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                      <Button
                        variant="link"
                        onClick={() => {
                          navigate({
                            to: "/patients/patient-info/$patientID",
                            params: {
                              patientID: item.custoemrId,
                            },
                            search: searchParams,
                          });
                        }}
                      >
                        <MoveRightIcon className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </>
        )}
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{data?.items && data.items.length > 0 ? data.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: 1
                    }
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page - 1
                    }
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {
                const itemsPerPage = searchParams.pageSize;
                const totalPages = data?.totalCount ? Math.ceil(data.totalCount / itemsPerPage ) : 1;
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (searchParams.page > 3) pages.push('...');
                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (searchParams.page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((p, idx) => (
                  p === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={p}
                        active={searchParams.page === p}
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: typeof p === 'number' ? p : searchParams.page
                            }
                          });
                        }}
                      >
                        <span className="text-sm">{p}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page + 1
                    }
                  });
                }}
                disabled={true /* No real pagination without full_count */}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: 1
                    }
                  });
                }}
                disabled={true /* No real pagination without full_count */}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Component;
